import React, { FC, useRef, useState } from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ThemeContext } from '../../../module/ThemeManager';
import { theme } from '../../../constants/theme';
import { IContest, IProject } from '../../../types/global/helper';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { IPagination } from '../../../hook/usePagination';
import { isEmpty } from '../../../utils/lodash';
import { IContestSubmissionsResponse } from '../../../redux/contest/types';
import { CustomPagination } from '../../Tables/Pagination';
import close from '../../../asset/images/icons/es_x_close.svg';
import { SearchIcon } from '@chakra-ui/icons';
import { ContestHero } from '../../EsHero/ContestHero';
import { ContestSubmissionCard } from '../../EsCards/ContestSubmissionCard';
import _ from 'lodash';

interface IContestSubmissionsContainerProps {
    contest: IContest | null;
    submissions: IContestSubmissionsResponse['data'];
    loading: boolean;
    searching: boolean;
    error: any;
    pagination: IPagination;
    searchTerm?: string;
    sortOption: string;
    orderOption: 'asc' | 'desc';
    availableFilters: { filter: string; display: string }[];
    onSearchChange: (searchText: string) => void;
    onSortOptionChange: (option: string) => void;
    onOrderChange: (order: 'asc' | 'desc') => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onPlay?: (project: IProject) => void;
    onClearSearch: () => void;
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 120,
    margin: '0 8px',
}));

export const ContestSubmissionsContainer: FC<IContestSubmissionsContainerProps> = ({
    contest,
    submissions,
    loading,
    searching,
    error,
    pagination,
    searchTerm,
    sortOption,
    orderOption,
    availableFilters,
    onSearchChange,
    onSortOptionChange,
    onOrderChange,
    onRowsChange,
    onPageChange,
    onPlay,
    onClearSearch,
}) => {
    const [value, setValue] = useState('');

    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            if (onSearchChange) {
                onSearchChange(searchText);
            }
        }, 500),
    ).current;

    const handleClearSearch = () => {
        setValue('');
        onClearSearch && onClearSearch();
        // Cancel any pending debounced searches
    };

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setValue(newValue);
        debouncedSearchHandler(newValue);
    };
    return (
        <ThemeContext.Consumer>
            {({ themeStyle }) => {
                return (
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={{
                            py: 4,
                            overflow: 'hidden',
                            mt: { xs: 2, lg: 0 },
                            p: { xs: 2, sm: 4, smMd: 6 },
                            ml: { xs: 0, sm: 8, xl: '100px' },
                            width: { xs: '100%', sm: 'auto', smMd: 'auto', xl: 'auto' },
                            minHeight: '100vh',
                        }}>
                        {/* Contest Hero */}
                        <ContestHero contest={contest} />

                        <Box sx={{ width: '100%', mb: 4 }}>
                            {/* Search and Filter Controls */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 4,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 2, sm: 0 },
                                }}>
                                <Box
                                    sx={{
                                        width: { xs: '100%' },
                                        position: 'relative',
                                    }}>
                                    <Box sx={{ width: { xs: '100%', sm: '40%' } }}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder="Search submissions by username or project title..."
                                            value={value}
                                            onChange={handleSearchInputChange}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: value ? (
                                                    <InputAdornment position="end">
                                                        <img
                                                            src={close}
                                                            onClick={handleClearSearch}
                                                            alt="close icon"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '5px',
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].grey,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].tag_white,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].primary_orange,
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                    {searching && (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                position: 'absolute',
                                                right: 10,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: theme[themeStyle].primary_orange,
                                            }}
                                        />
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        width: { xs: '100%', sm: 'auto' },
                                    }}>
                                    <StyledFormControl
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].grey,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme[themeStyle].primary_orange,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                        }}>
                                        <InputLabel>Sort by</InputLabel>
                                        <Select
                                            value={sortOption}
                                            onChange={e => onSortOptionChange(e.target.value as string)}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: theme[themeStyle].secondaryBg,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& .MuiMenuItem-root': {
                                                            // hover state
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    theme[themeStyle].highlight_to_light_grey,
                                                            },

                                                            // selected option
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].role_hover,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                                backgroundColor: theme[themeStyle].form_fields,
                                                color: theme[themeStyle].tag_white,
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].tag_white,
                                                },
                                            }}
                                            label="Sort by">
                                            {availableFilters.map(filter => (
                                                <MenuItem key={filter.filter} value={filter.filter}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            fontSize: '16px',
                                                            color: theme[themeStyle].tag_white,
                                                            textTransform: 'capitalize',
                                                        }}>
                                                        {filter.display}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </StyledFormControl>

                                    <StyledFormControl
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme[themeStyle].tag_white,
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].grey,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme[themeStyle].primary_orange,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                        }}>
                                        <InputLabel>Order</InputLabel>
                                        <Select
                                            value={orderOption}
                                            onChange={e => onOrderChange(e.target.value as 'asc' | 'desc')}
                                            label="Order"
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: theme[themeStyle].secondaryBg,
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '& .MuiMenuItem-root': {
                                                            // hover state
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    theme[themeStyle].highlight_to_light_grey,
                                                            },

                                                            // selected option
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme[themeStyle].role_hover,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].form_divider,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme[themeStyle].highlight,
                                                },
                                                backgroundColor: theme[themeStyle].form_fields,
                                                color: theme[themeStyle].tag_white,
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].tag_white,
                                                },
                                            }}>
                                            <MenuItem value="asc">
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    Ascending
                                                </Typography>
                                            </MenuItem>
                                            <MenuItem value="desc">
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    Descending
                                                </Typography>
                                            </MenuItem>
                                        </Select>
                                    </StyledFormControl>
                                </Box>
                            </Box>

                            {/* Submissions Grid */}
                            {loading && !searching ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                    <CircularProgress size={40} sx={{ color: theme[themeStyle].primary_orange }} />
                                </Box>
                            ) : isEmpty(submissions) ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <Typography variant="h6" sx={{ color: theme[themeStyle].filters_white }}>
                                        No submissions found
                                    </Typography>
                                    {searchTerm && (
                                        <Typography
                                            variant="body1"
                                            onClick={onClearSearch}
                                            sx={{
                                                color: theme[themeStyle].primary_orange,
                                                cursor: 'pointer',
                                                mt: 2,
                                            }}>
                                            Clear search
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    {submissions.map(
                                        submission =>
                                            submission &&
                                            submission.project && (
                                                <Grid item xs={12} sm={6} md={4} key={submission._id}>
                                                    <ContestSubmissionCard
                                                        project={submission.project}
                                                        user={submission.user}
                                                        total_votes={submission.total_votes}
                                                        position={submission.position}
                                                        onPlay={() => {
                                                            /** Creating the Voting payload */
                                                            const payload: any = {
                                                                ...submission.project,
                                                                submissions: [
                                                                    {
                                                                        ...submission,
                                                                        contest,
                                                                    },
                                                                ],
                                                            };
                                                            onPlay && onPlay(payload);
                                                        }}
                                                        awards={submission.awards}
                                                    />
                                                </Grid>
                                            ),
                                    )}
                                </Grid>
                            )}

                            {/* Pagination */}
                            {!isEmpty(submissions) && pagination.totalCount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <CustomPagination
                                        pagination={pagination}
                                        rowsPerPageOptions={[6, 9, 12, 24]}
                                        onRowsChange={onRowsChange}
                                        onPageChange={onPageChange}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Container>
                );
            }}
        </ThemeContext.Consumer>
    );
};
